import { useCallback, useEffect, useState, type KeyboardEvent, type ReactNode } from "react";

import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import {
  MonolithBoardModal,
  MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS,
  MONOLITH_BOARD_MODAL_INPUT_CLASS,
  MONOLITH_BOARD_MODAL_TITLE_CLASS,
} from "@/components/dashboard/MonolithBoardModal";
import { Button } from "@/components/ui/Button";
import type { CampaignMemberRole, CampaignRosterMember, UserCampaignSummary } from "@west-marches/shared";
import { cn } from "@/lib/classNames";

export function CampaignTitleControl({
  campaign,
  canEdit,
  onRenameCampaign,
  renamingCampaign,
  className,
  tone = "paper",
}: {
  campaign: UserCampaignSummary;
  canEdit: boolean;
  onRenameCampaign: (name: string) => Promise<void>;
  renamingCampaign: boolean;
  className?: string;
  tone?: "paper" | "dark";
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(campaign.name);

  useEffect(() => {
    setDraft(campaign.name);
  }, [campaign.name]);

  const startEditing = useCallback(() => {
    if (!canEdit || renamingCampaign) {
      return;
    }
    setDraft(campaign.name);
    setEditing(true);
  }, [campaign.name, canEdit, renamingCampaign]);

  const stopEditing = useCallback(() => {
    setEditing(false);
    setDraft(campaign.name);
  }, [campaign.name]);

  const handleSave = useCallback(async () => {
    const next = draft.trim();
    if (!next || next === campaign.name) {
      stopEditing();
      return;
    }
    await onRenameCampaign(next);
    setEditing(false);
  }, [campaign.name, draft, onRenameCampaign, stopEditing]);

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.currentTarget.blur();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        stopEditing();
      }
    },
    [stopEditing],
  );

  const headingClass =
    tone === "dark"
      ? "text-xl font-black tracking-tight text-white sm:text-2xl"
      : "text-xl font-black tracking-tight text-neutral-950 sm:text-2xl";
  const hintClass =
    tone === "dark"
      ? "mt-2 text-sm text-white/75"
      : "mt-2 text-sm text-neutral-600";
  const labelClass =
    tone === "dark"
      ? "text-[10px] uppercase tracking-[0.12em] text-white/80"
      : "text-[10px] uppercase tracking-[0.12em] text-neutral-600";
  const inputClass =
    tone === "dark"
      ? "border-[3px] border-white/70 bg-white/10 px-3 py-2 text-base font-bold tracking-tight text-white placeholder:text-white/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      : "w-full border-[3px] border-neutral-950 bg-[#fffaf1] px-3 py-2 text-base font-bold tracking-tight text-neutral-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-950";

  if (!canEdit) {
    return (
      <div className={className}>
        <h2 className={headingClass}>{campaign.name}</h2>
        <p className={hintClass}>
          Renaming is reserved for the campaign owner and game masters.
        </p>
      </div>
    );
  }

  if (editing) {
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        <label className={labelClass} htmlFor="campaign-name-edit">
          Campaign name
        </label>
        <input
          id="campaign-name-edit"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className={inputClass}
          disabled={renamingCampaign}
          autoFocus
          onFocus={(event) => event.currentTarget.select()}
          onBlur={() => {
            void handleSave();
          }}
          onKeyDown={handleInputKeyDown}
        />
        <p className={hintClass}>{renamingCampaign ? "Saving..." : "Press Enter or click away to save."}</p>
      </div>
    );
  }

  const editButtonClass =
    tone === "dark"
      ? "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-white/80 bg-white/10 text-white transition-colors hover:bg-white/20"
      : cn(
          "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-neutral-950 transition-colors",
          MONOLITH_BOARD_STYLE.mutedButtonClassName,
        );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-start gap-2">
        <h2
          className={cn(headingClass, canEdit && "cursor-text")}
          onDoubleClick={canEdit ? startEditing : undefined}
          title={canEdit ? "Double-click to rename" : undefined}
        >
          {campaign.name}
        </h2>
        {canEdit ? (
          <button
            type="button"
            onClick={startEditing}
            className={editButtonClass}
            aria-label="Edit campaign name"
            title="Double-click the title to rename"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="2">
              <path d="M4 20h4l10-10-4-4L4 16v4Z" />
              <path d="m13 7 4 4" />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard API unavailable */
    }
  }, [value]);

  return (
    <Button
      type="button"
      variant="secondary"
      chrome="compact"
      className="px-3 py-1.5 text-[10px]"
      onClick={() => { void handleCopy(); }}
      aria-label={label}
    >
      {copied ? "Copied!" : "Copy"}
    </Button>
  );
}

export function JoinCodePanel({
  campaign,
  onRotateJoinCode,
  rotatingJoinCode,
  onToggleSelfJoin,
  togglingSelfJoin,
  actionSlot,
}: {
  campaign: UserCampaignSummary;
  onRotateJoinCode: () => Promise<void>;
  rotatingJoinCode: boolean;
  onToggleSelfJoin: (enabled: boolean) => Promise<void>;
  togglingSelfJoin: boolean;
  actionSlot?: ReactNode;
}) {
  const inviteLink = campaign.joinCode
    ? `${window.location.origin}/campaign/${campaign.shortId}/join?code=${encodeURIComponent(campaign.joinCode)}`
    : null;
  const busy = rotatingJoinCode || togglingSelfJoin;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-600">Invite link</p>
        <p className="mt-2 text-[11px] leading-snug text-neutral-600">
          Share this link with players so they can join the campaign with one click.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div
            className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap border-[3px] border-neutral-950 bg-[#fffaf1] px-3 py-2 text-[11px] text-neutral-700"
            aria-label="Current invite link"
          >
            {inviteLink ?? "No invite link generated yet"}
          </div>
          {inviteLink ? (
            <CopyButton value={inviteLink} label="Copy invite link" />
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="primary"
            chrome="compact"
            className="px-3 py-2 text-[10px]"
            disabled={busy}
            onClick={() => { void onRotateJoinCode(); }}
          >
            {rotatingJoinCode
              ? "Generating..."
              : campaign.joinCode
                ? "Rotate invite link"
                : "Generate invite link"}
          </Button>
          {actionSlot}
        </div>

        <label className="flex items-center gap-2 text-[11px] text-neutral-700">
          <input
            type="checkbox"
            checked={campaign.selfJoinEnabled}
            disabled={busy}
            onChange={(e) => { void onToggleSelfJoin(e.target.checked); }}
            className="accent-[#3e664d]"
          />
          <span>Allow players to self-join with this link</span>
        </label>

        <p className="text-[11px] leading-snug text-neutral-600">
          Rotating the link invalidates the previous one — anyone still holding the old link will need a new one.
        </p>
      </div>
    </div>
  );
}

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  gm: "GM",
  player: "Player",
};

export function RosterManagementPanel({
  campaign,
  canManageMembers = false,
  onUpdateRole,
  updatingRole,
  onRemoveMember,
  removingMember,
}: {
  campaign: UserCampaignSummary;
  canManageMembers?: boolean;
  onUpdateRole: (userId: string, role: CampaignMemberRole) => Promise<void>;
  updatingRole: boolean;
  onRemoveMember: (userId: string) => Promise<void>;
  removingMember: boolean;
}) {
  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState<string | null>(null);
  const busy = updatingRole || removingMember;

  const handleRemove = useCallback(
    async (userId: string) => {
      await onRemoveMember(userId);
      setConfirmRemoveUserId(null);
    },
    [onRemoveMember],
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-600">Members</p>
        <p className="mt-2 text-[11px] leading-snug text-neutral-600">
          {canManageMembers
            ? "Manage campaign members — promote, demote, or remove players."
            : "See who is part of this campaign right now."}
        </p>
      </div>

      <ul className="divide-y-2 divide-neutral-950/10 border-[3px] border-neutral-950 bg-[#fffaf1]">
        {campaign.roster.map((member: CampaignRosterMember) => (
          <li
            key={member.userId}
            className="flex items-center gap-3 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-neutral-950">
                {member.displayName ?? "Unknown player"}
              </p>
              <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-500">
                {ROLE_LABELS[member.kind] ?? member.kind}
              </p>
            </div>

            {!canManageMembers || member.kind === "owner" ? null : confirmRemoveUserId === member.userId ? (
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="cancel"
                  chrome="compact"
                  className="px-2 py-1 text-[9px]"
                  disabled={busy}
                  onClick={() => setConfirmRemoveUserId(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  chrome="compact"
                  className="px-2 py-1 text-[9px]"
                  disabled={busy}
                  onClick={() => { void handleRemove(member.userId); }}
                >
                  {removingMember ? "Removing..." : "Confirm"}
                </Button>
              </div>
            ) : (
              <div className="flex gap-1">
                {member.kind === "gm" ? (
                  <Button
                    type="button"
                    variant="secondary"
                    chrome="compact"
                    className="px-2 py-1 text-[9px]"
                    disabled={busy}
                    onClick={() => { void onUpdateRole(member.userId, "player"); }}
                    aria-label={`Make ${member.displayName ?? "member"} a player`}
                  >
                    Make Player
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    chrome="compact"
                    className="px-2 py-1 text-[9px]"
                    disabled={busy}
                    onClick={() => { void onUpdateRole(member.userId, "gm"); }}
                    aria-label={`Make ${member.displayName ?? "member"} a GM`}
                  >
                    Make GM
                  </Button>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  chrome="compact"
                  className="px-2 py-1 text-[9px]"
                  disabled={busy}
                  onClick={() => setConfirmRemoveUserId(member.userId)}
                  aria-label={`Remove ${member.displayName ?? "member"}`}
                >
                  Remove
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const DELETE_CONFIRMATION_PHRASE = "Delete campaign";

export function CampaignDeleteControl({
  canDelete,
  campaignName,
  onDeleteCampaign,
  deletingCampaign,
  className,
}: {
  canDelete: boolean;
  campaignName: string;
  onDeleteCampaign: () => void;
  deletingCampaign: boolean;
  className?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [phraseInput, setPhraseInput] = useState("");

  const canConfirm =
    nameInput === campaignName && phraseInput === DELETE_CONFIRMATION_PHRASE;

  function handleClose() {
    if (deletingCampaign) return;
    setModalOpen(false);
    setNameInput("");
    setPhraseInput("");
  }

  if (!canDelete) {
    return null;
  }
  return (
    <div className={cn("space-y-3", className)}>
      <p className="text-[10px] uppercase tracking-[0.12em] text-neutral-600">Danger zone</p>
      <Button
        type="button"
        onClick={() => setModalOpen(true)}
        disabled={deletingCampaign}
        variant="destructive"
        chrome="compact"
        className="px-3 py-2.5 text-xs tracking-[0.14em]"
      >
        Delete campaign
      </Button>
      <p className="text-[11px] leading-snug text-neutral-600">Permanently removes this campaign and its map data.</p>

      <MonolithBoardModal
        open={modalOpen}
        titleId="delete-campaign-modal-title"
        onClose={handleClose}
        submitting={deletingCampaign}
      >
        <h2 id="delete-campaign-modal-title" className={MONOLITH_BOARD_MODAL_TITLE_CLASS}>
          Delete campaign
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-neutral-700">
          This action <strong>permanently removes</strong> this campaign and all its map data.
          This cannot be undone.
        </p>

        <div className="mt-6 space-y-5">
          <div>
            <label htmlFor="delete-confirm-name" className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>
              Enter the campaign name <span className="font-bold normal-case tracking-normal text-neutral-950">"{campaignName}"</span>
            </label>
            <input
              id="delete-confirm-name"
              type="text"
              autoComplete="off"
              className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              disabled={deletingCampaign}
            />
          </div>
          <div>
            <label htmlFor="delete-confirm-phrase" className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>
              Type <span className="font-bold normal-case tracking-normal text-neutral-950">"{DELETE_CONFIRMATION_PHRASE}"</span> to confirm
            </label>
            <input
              id="delete-confirm-phrase"
              type="text"
              autoComplete="off"
              className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
              value={phraseInput}
              onChange={(e) => setPhraseInput(e.target.value)}
              disabled={deletingCampaign}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="cancel"
            chrome="compact"
            disabled={deletingCampaign}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            chrome="compact"
            disabled={!canConfirm || deletingCampaign}
            onClick={onDeleteCampaign}
          >
            {deletingCampaign ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </MonolithBoardModal>
    </div>
  );
}

